/**
 * @file Miscellaneous helper functions.
 *
 * Please find an appropriate subdirectory and avoid adding new functions to this file.
 */

/**
 * Executes a callback function only once for a given unique key.
 *
 * @param callback - The function to be executed.
 * @param uniqueKey - A unique identifier to ensure the callback is only run once.
 */
export function runOnce(callback: () => void, uniqueKey: string): void {
  // Initialize the keys property if it doesn't exist
  if (!runOnce.keys) {
    runOnce.keys = [];
  }

  if (!runOnce.keys.includes(uniqueKey)) {
    runOnce.keys.push(uniqueKey);
    callback();
  }
}

// Define the keys property on the runOnce function
runOnce.keys = [] as string[];

/**
 * Retrieves a nested string value from an object based on a dot-separated key path.
 *
 * @param obj - The object or string to retrieve the value from.
 * @param key - The dot-separated key path to the desired value.
 * @returns The nested string value if found, otherwise an empty string.
 *
 * @example
 * const obj = {
 *   a: {
 *     b: "1"
 *   },
 *   c: 45
 * };
 *
 * getNestedStringValue(obj, "a.b"); // "1"
 * getNestedStringValue(obj, "a.c"); // ""
 * getNestedStringValue(obj, "a");   // ""
 * getNestedStringValue(obj, "c");   // ""
 */
export const getNestedStringValue = (obj: object | string, key: string): string => {
  if (!obj || typeof key !== "string" || key.trim() === "") {
    return "";
  }
  const value = key.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
  return typeof value === "string" ? value : "";
};

/**
 * Joins the given class names together, filtering out any falsy values.
 *
 * @param classes - The class names to join together.
 * @returns The concatenated class names.
 *
 * @example
 * cn("class1", "class2", undefined, "class3"); // "class1 class2 class3"
 */
export function combineClasses(...classes: (string | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Creates a promise that will reject if the original promise doesn't resolve within the specified timeout.
 * @param promise The promise to wrap with a timeout
 * @param timeoutMs The timeout in milliseconds
 * @returns A promise that will either resolve with the original promise's value or reject with a timeout error
 */
export function createTimeoutPromise<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  if (timeoutMs <= 0) {
    return Promise.reject(new Error("Request timed out"));
  }

  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), timeoutMs)
    )
  ]);
}
